import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axiosInstance';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './CartPage.css';

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    fetchCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    loading,
  } = useCartStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    fetchCart();

    // Handle restore_order param from dashboard "Complete Payment" button
    const params = new URLSearchParams(window.location.search);
    const restoreOrder = params.get('restore_order');
    if (restoreOrder && isAuthenticated()) {
      try {
        const items = JSON.parse(decodeURIComponent(restoreOrder));
        Promise.all(
          items.map((item) =>
            axiosInstance.post('/cart', {
              productId: item.productId,
              quantity: item.quantity,
            })
          )
        ).then(() => {
          fetchCart();
          navigate('/checkout');
        }).catch(() => {
          // If restore fails just stay on cart
          fetchCart();
        });
      } catch (e) {
        // Invalid restore_order param, ignore
      }
    }
  }, []);

  const handleQuantityChange = async (cartItemId, currentQty, change) => {
    const newQty = currentQty + change;
    if (newQty <= 0) {
      await removeFromCart(cartItemId);
    } else {
      await updateCartItem(cartItemId, newQty);
    }
  };

  const total = getCartTotal();

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="cart-page">
          <div className="container">
            <div className="cart-loading">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="cart-item-skeleton" />
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="cart-page">
        <div className="container">
          <h1 className="cart-title">
            <ShoppingBag size={28} />
            Your Cart
            {cartItems.length > 0 && (
              <span className="cart-count">{cartItems.length} items</span>
            )}
          </h1>

          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <ShoppingBag size={64} color="#29ABE2" strokeWidth={1} />
              <h2>Your cart is empty</h2>
              <p>Add some fresh seafood to get started</p>
              <Link to="/shop" className="hero-btn-primary">
                Browse Products <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <div className="cart-layout">
              {/* Cart Items */}
              <div className="cart-items">
                <div className="cart-items-header">
                  <span>Product</span>
                  <span>Quantity</span>
                  <span>Total</span>
                </div>

                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-product">
                      <div className="cart-item-image">
                        {item.product?.imageUrl ? (
                          <img
                            src={item.product.imageUrl}
                            alt={item.product.name}
                          />
                        ) : (
                          <ShoppingBag size={24} color="#29ABE2" />
                        )}
                      </div>
                      <div className="cart-item-details">
                        <h4>{item.product?.name}</h4>
                        <p className="cart-item-category">
                          {item.product?.category?.name}
                        </p>
                        <p className="cart-item-price">
                          GHS {item.unitPrice?.toFixed(2)} each
                        </p>
                      </div>
                    </div>

                    <div className="cart-item-quantity">
                      <button
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity, -1)
                        }
                      >
                        <Minus size={14} />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity, 1)
                        }
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <div className="cart-item-total">
                      <span>GHS {(item.unitPrice * item.quantity).toFixed(2)}</span>
                      <button
                        className="remove-btn"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                <button className="clear-cart-btn" onClick={clearCart}>
                  <Trash2 size={14} />
                  Clear Cart
                </button>
              </div>

              {/* Order Summary */}
              <div className="cart-summary">
                <h3 className="summary-title">Order Summary</h3>

                <div className="summary-rows">
                  <div className="summary-row">
                    <span>Subtotal ({cartItems.length} items)</span>
                    <span>GHS {total.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Delivery</span>
                    <span className="free-delivery">Calculated at checkout</span>
                  </div>
                  <div className="summary-row">
                    <span>Loyalty Discount</span>
                    <span className="discount-text">Applied at checkout</span>
                  </div>
                </div>

                <div className="summary-total">
                  <span>Estimated Total</span>
                  <span>GHS {total.toFixed(2)}</span>
                </div>

                <div className="loyalty-note">
                  <p>
                    You'll earn approximately{' '}
                    <strong>{Math.floor(total)} Aurora Points</strong>{' '}
                    on this order
                  </p>
                </div>

                <button
                  className="checkout-btn"
                  onClick={() => navigate('/checkout')}
                >
                  Proceed to Checkout <ArrowRight size={18} />
                </button>

                <Link to="/shop" className="continue-shopping">
                  Continue Shopping
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CartPage;